/**
 * A chat message as meetings-sockets rebroadcasts it. The server sets `identity`, `id` and
 * `created` from the verified connection; `body` is the text typed by the sender.
 */
export type ChatMessage = {
  id?: string;
  identity: string;
  body: string[];
  created: string | Date;
};

// Same cap as meetings-sockets applies before relaying (helpers/handshake.js).
// `body` is an array because that is the shape the groups on screen use; a message holds exactly one string.
export const CHAT_MESSAGE_MAX_LENGTH = 2000;

/**
 * Type guard applied to every payload received on the chat socket before it reaches React state,
 * so an unexpected shape is dropped instead of crashing the call screen.
 */
export function isChatMessage(value: unknown): value is ChatMessage {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  const candidate = value as Record<string, unknown>;

  if (typeof candidate.identity !== 'string' || candidate.identity.length === 0) return false;
  if (candidate.id !== undefined && typeof candidate.id !== 'string') return false;

  const { body, created } = candidate;
  if (!Array.isArray(body) || body.length !== 1) return false;
  if (typeof body[0] !== 'string' || body[0].length > CHAT_MESSAGE_MAX_LENGTH) return false;

  if (typeof created !== 'string' && !(created instanceof Date)) return false;
  return !Number.isNaN(new Date(created).getTime());
}
